import React, { useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { BusinessCard, PERSON_TYPE } from '../index'
import { useBusinessCard } from '../context'
import styled from 'styled-components'
import { H5, P, spacing, colors } from '@PipelineDeals/shared-react-components'
import { ATTRIBUTES_SECTION_MAX_HEIGHT_INSIDE_POPOVER } from './StyledComponents'
import { Swiper, SwiperSlide } from 'swiper/react'
require('style-loader!css-loader!swiper/css') // eslint-disable-line import/no-webpack-loader-syntax

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${spacing['3xl']};
  max-width: 375px;
`

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: ${spacing.md};
  width: 100%;
`

const HeaderSecondaryContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.md};
  width: 100%;
`

const BodyContainer = styled.div`
  width: 100%;
`

const Navigation = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.md};
`

const NavigationLink = styled.a`
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  color: ${({ disabled, theme }) => (disabled ? theme.colors.gray[400] : theme.colors.gray[800])};

  &:hover {
    color: ${({ disabled, theme }) => (disabled ? theme.colors.gray[400] : theme.colors.gray[800])};
  }
`

const NavigationIcon = styled.i`
  font-size: 14px;
`

const DealTitle = styled(H5)`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
`

const DealPeopleCarousel = () => {
  const { t } = useTranslation()
  const [isFirst, setIsFirst] = useState(true)
  const [isLast, setIsLast] = useState(false)
  const swiperRef = useRef()

  const {
    callbacks: { onClose },
    resource: deal
  } = useBusinessCard()

  const personIds = deal.person_ids || []

  const handleSlideChange = (swiper) => {
    setIsFirst(swiper.isBeginning)
    setIsLast(swiper.isEnd)
  }

  const renderBody = () => {
    if (personIds.length === 0) {
      return <P color={colors.gray[500]}>{t('businessCard.noPeopleFound')}</P>
    }

    if (personIds.length === 1) {
      return (
        <BusinessCard
          type={PERSON_TYPE}
          itemId={personIds[0]}
          onClose={onClose}
          attributesSectionScrollable
          attributesSectionMaxHeight={ATTRIBUTES_SECTION_MAX_HEIGHT_INSIDE_POPOVER}
        />
      )
    }

    if (personIds.length > 1) {
      return (
        <Swiper
          onSwiper={(swiper) => (swiperRef.current = swiper)}
          allowTouchMove={false}
          autoHeight
          onSlideChange={handleSlideChange}
          spaceBetween={20}
          slidesPerView={1}
        >
          {personIds.map((personId, index) => (
            <SwiperSlide
              key={personId}
              autoHeight
            >
              <BusinessCard
                // When the first BusinessCard is mounted, and its data fetched, that's when the BusinessCard component gets its final height.
                // So we need to update the Swiper instance to get the correct height for the BodyContainer component, because autoHeight is set to true.
                onItemFetched={index === 0 ? () => swiperRef.current.update() : null}
                type={PERSON_TYPE}
                itemId={personId}
                onClose={onClose}
                attributesSectionScrollable
                attributesSectionMaxHeight={ATTRIBUTES_SECTION_MAX_HEIGHT_INSIDE_POPOVER}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      )
    }
  }

  return (
    <MainContainer>
      <HeaderContainer>
        <DealTitle>{deal.name}</DealTitle>
        <HeaderSecondaryContainer>
          <P color={colors.gray[400]}>{t('businessCard.labels.owner')}: {deal.user.first_name} {deal.user.last_name}</P>
          {personIds.length > 1 && (
            <Navigation>
              <NavigationLink
                onClick={() => swiperRef.current?.slidePrev()}
                disabled={isFirst}
              >
                <NavigationIcon className="fas fa-arrow-left" />
              </NavigationLink>
              <NavigationLink
                onClick={() => swiperRef.current?.slideNext()}
                disabled={isLast}
              >
                <NavigationIcon className="fas fa-arrow-right" />
              </NavigationLink>
            </Navigation>
          )}
        </HeaderSecondaryContainer>
      </HeaderContainer>
      <BodyContainer>
        {renderBody()}
      </BodyContainer>
    </MainContainer>
  )
}

export default DealPeopleCarousel
